<script setup>
// CRIAR / EDITAR URA (telefonia, etapa E — 25/09/2026): o áudio que o cliente
// ouve ("digite 1 para…"), o que cada tecla faz e para onde vai quem não
// digita nada. O áudio pode ser qualquer arquivo de som (wav, mp3, o m4a do
// gravador do celular): a central converte na primeira ligação.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { opcoesDeDestino } from './destinos';
import AudioEnviado from './AudioEnviado.vue';

const props = defineProps({
  ura: { type: Object, default: null },
  contexto: { type: Object, required: true },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});
const emit = defineEmits(['close', 'salvar']);

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO.URA';
const editando = computed(() => Boolean(props.ura));

const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const nome = ref(props.ura?.nome || '');
const espera = ref(props.ura?.espera ?? 6);
const senao = ref(props.ura?.senao || '');
const direto = ref(Boolean(props.ura?.direto));
const opcoes = ref(
  Object.fromEntries(TECLAS.map(k => [k, props.ura?.opcoes?.[k] || '']))
);
const tentou = ref(false);

// ── Áudio (AudioEnviado.vue) ────────────────────────────────────────────
const arquivo = ref(null);
const tirarAudio = ref(false);

const opcoesDeTecla = computed(() =>
  opcoesDeDestino(props.contexto, t, {
    excluir: props.ura ? `ura:${props.ura.id}` : '',
    comVazio: t(`${T}.TECLA_VAZIA`),
  })
);
const opcoesDoSenao = computed(() =>
  opcoesDeDestino(props.contexto, t, {
    excluir: props.ura ? `ura:${props.ura.id}` : '',
    comVazio: t('TELEFONIA.ROTEAMENTO.DESTINO.NENHUM'),
  })
);
const usadas = computed(() => TECLAS.filter(k => opcoes.value[k]).length);

const erroDoNome = computed(() =>
  nome.value.trim() ? '' : t('TELEFONIA.ROTEAMENTO.FILA.NOME_FALTA')
);

function salvar() {
  tentou.value = true;
  if (erroDoNome.value) return;
  const dados = new FormData();
  dados.append('name', nome.value.trim());
  dados.append('wait_seconds', String(espera.value));
  dados.append('fallback_destination', senao.value || '');
  dados.append('opcoes', JSON.stringify(opcoes.value));
  dados.append('direct_dial', String(direto.value));
  if (arquivo.value) dados.append('audio', arquivo.value);
  if (tirarAudio.value) dados.append('remover_audio', 'true');
  emit('salvar', dados);
}
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div v-modal-acessivel="() => emit('close')" class="advup-modal tel-ura">
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-list-numbers-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">
            {{
              editando ? t(`${T}.EDITAR`, { nome: ura.nome }) : t(`${T}.NOVA`)
            }}
          </h3>
          <p class="advup-modal__sub">{{ t(`${T}.SUB`) }}</p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t('TELEFONIA.ROTEAMENTO.CANCELAR')"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <form class="advup-modal__corpo tel-ura__corpo" @submit.prevent="salvar">
        <label class="tel-ura__campo">
          <span class="advup-rotulo">{{
            t('TELEFONIA.ROTEAMENTO.FILA.NOME')
          }}</span>
          <input
            v-model="nome"
            name="nome-da-ura"
            type="text"
            maxlength="60"
            autocomplete="off"
            class="advup-campo reset-base"
            :placeholder="t(`${T}.NOME_PH`)"
            :aria-invalid="Boolean(tentou && erroDoNome)"
          />
          <span v-if="tentou && erroDoNome" class="advup-nota advup-nota--erro">
            {{ erroDoNome }}
          </span>
        </label>

        <!-- O áudio: tocador quando existe, área de envio quando não -->
        <AudioEnviado
          v-model:arquivo="arquivo"
          v-model:tirar="tirarAudio"
          :atual="ura?.audio || null"
          :rotulo="t(`${T}.AUDIO`)"
          :dica="t(`${T}.AUDIO_DICA`)"
        />

        <!-- As teclas: um teclado de telefone de verdade -->
        <div class="tel-ura__campo">
          <span class="advup-rotulo">
            {{ t(`${T}.TECLAS`) }}
            <span class="tel-ura__conta">{{
              t(`${T}.USADAS`, { n: usadas })
            }}</span>
          </span>
          <span class="advup-nota">{{ t(`${T}.TECLAS_DICA`) }}</span>
          <ul class="tel-ura__teclas">
            <li
              v-for="k in TECLAS"
              :key="k"
              class="tel-ura__tecla"
              :class="{ 'tel-ura__tecla--sim': opcoes[k] }"
            >
              <span class="tel-ura__digito" aria-hidden="true">{{ k }}</span>
              <AdvupSelect
                v-model="opcoes[k]"
                :options="opcoesDeTecla"
                :rotulo="t('TELEFONIA.ROTEAMENTO.TECLA', { tecla: k })"
                min-width="0"
              />
            </li>
          </ul>
        </div>

        <button
          type="button"
          role="switch"
          class="advup-chave"
          :aria-checked="String(direto)"
          @click="direto = !direto"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.DIRETO`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.DIRETO_DICA`) }}</span>
          </span>
        </button>

        <div class="tel-ura__duplo">
          <label class="tel-ura__campo">
            <span class="advup-rotulo">{{ t(`${T}.ESPERA`) }}</span>
            <span class="tel-ura__num">
              <input
                v-model.number="espera"
                name="espera-da-ura"
                type="number"
                min="1"
                max="30"
                class="advup-campo reset-base"
              />
              <span>{{ t('TELEFONIA.ROTEAMENTO.SEGUNDOS') }}</span>
            </span>
            <span class="advup-nota">{{ t(`${T}.ESPERA_DICA`) }}</span>
          </label>
          <div class="tel-ura__campo">
            <span class="advup-rotulo">{{ t(`${T}.SENAO`) }}</span>
            <AdvupSelect
              v-model="senao"
              :options="opcoesDoSenao"
              :rotulo="t(`${T}.SENAO`)"
              min-width="100%"
            />
            <span class="advup-nota">{{ t(`${T}.SENAO_DICA`) }}</span>
          </div>
        </div>

        <p v-if="erro" class="tel-ura__erro" role="alert">
          <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
          <span>{{ erro }}</span>
        </p>
      </form>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t('TELEFONIA.ROTEAMENTO.CANCELAR') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'
            "
            aria-hidden="true"
          />
          {{
            salvando
              ? t('TELEFONIA.ROTEAMENTO.SALVANDO')
              : editando
                ? t('TELEFONIA.ROTEAMENTO.SALVAR')
                : t('TELEFONIA.ROTEAMENTO.CRIAR')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-ura__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}
.tel-ura__campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
}
.tel-ura__conta {
  margin-left: var(--esp-1);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--teal-11));
}
.tel-ura__teclas {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-ura__tecla {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-1);
  border-radius: var(--r-3);
  transition: background var(--mov-1) var(--mov-curva);
}
.tel-ura__tecla > :last-child {
  flex: 1;
  min-width: 0;
}
.tel-ura__digito {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  font-family: var(--fonte-display);
  font-size: var(--txt-bloco);
  font-weight: var(--peso-num);
  font-variant-numeric: tabular-nums;
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}
.tel-ura__tecla--sim .tel-ura__digito {
  background: rgb(var(--teal-9));
  color: white;
  transform: scale(1.05);
}
.tel-ura__duplo {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: var(--esp-4);
}
.tel-ura__num {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-ura__num .advup-campo {
  width: 80px;
  font-variant-numeric: tabular-nums;
}
.tel-ura__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-12));
}
@media (max-width: 639px) {
  .tel-ura__teclas,
  .tel-ura__duplo {
    grid-template-columns: 1fr;
  }
}
</style>
